import { useState } from "react"
import Api from "../api/api"
import Spinner from "./widgets/Spinner"

// Outcome of one link attempt, as the backend reports it: PACE linked the
// account on the spot (its email is this account's verified address),
// emailed a verification link to the ilok.com address, or found it linked
// already.
export type IlokLinkResult =
  | { kind: "linked", ilokId: string, issued: number }
  | { kind: "pending", ilokId: string, email: string }
  | { kind: "alreadyLinked", ilokId: string }

export function ilokLinkMessage(result: IlokLinkResult): string {
  switch (result.kind) {
    case "alreadyLinked":
      return "This iLok account is already linked to your account."
    case "linked":
      return result.issued > 0
        ? `Your iLok account is linked. ${result.issued === 1 ? "One license was" : `${result.issued} licenses were`} added to your account.`
        : "Your iLok account is linked. No eligible iLok licenses were found on it yet — anything that arrives later is picked up automatically."
    case "pending":
      return `We've emailed a verification link to ${result.email} — the address on your ilok.com account. Click it to finish linking.`
  }
}

// The "link an iLok account" form body shared by the Account page's link
// dialog and the Library's deposit dialog: hint, iLok User ID field, inline
// error and the Cancel/Link actions. It owns the attempt only — the host
// renders whatever follows a successful one (a success view, or back to
// its own step) and is told while a verification is in flight so it can
// keep its dialog from closing.
export default function IlokLinkForm({ accessToken, cancelLabel, onCancel, onBusyChange, onLinked }: {
  accessToken: string,
  cancelLabel: string,
  onCancel: () => void,
  onBusyChange: (busy: boolean) => void,
  onLinked: (result: IlokLinkResult) => void,
}) {
  const [step, setStep] = useState<"form" | "saving" | "error">("form")
  const [ilokId, setIlokId] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  const busy = step === "saving"
  const formValid = ilokId.trim() !== ""

  const link = async () => {
    const id = ilokId.trim()
    setStep("saving")
    onBusyChange(true)
    setErrorMessage("")
    try {
      const result = await Api.linkIlokAccount({ ilokId: id, accessToken })
      onBusyChange(false)
      if (result.alreadyLinked) {
        onLinked({ kind: "alreadyLinked", ilokId: id })
      } else if (result.linked) {
        onLinked({ kind: "linked", ilokId: id, issued: result.issued ?? 0 })
      } else {
        onLinked({ kind: "pending", ilokId: id, email: result.email ?? "" })
      }
    } catch (err) {
      onBusyChange(false)
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <>
      <p className="generate-codes-hint">
        iLok accounts registered to this account's email address are
        linked automatically. For one under a different address, enter
        its iLok User ID — we'll send a verification link to that
        ilok.com address to confirm it's yours.
      </p>
      <div className="generate-codes-form">
        <div className="generate-codes-field">
          <label className="generate-codes-label">iLok User ID</label>
          <input
            className="generate-codes-input"
            value={ilokId}
            onChange={(e) => setIlokId(e.target.value)}
            autoComplete="off"
            disabled={busy}
          />
        </div>
      </div>
      {step === "error" && errorMessage && <p className="generate-codes-error">{errorMessage}</p>}
      <div className="redeem-actions">
        <button onClick={onCancel} disabled={busy}>{cancelLabel}</button>
        <button className="btn-upgrade" onClick={link} disabled={!formValid || busy}>
          {busy ? <><Spinner size={12} /> Verifying…</> : "Link account"}
        </button>
      </div>
    </>
  )
}
